import { useCallback } from "react";

import {
    AbfLoanExpanded,
    useSwapCollateralTransaction,
    AbfTransactionDetails,
    useBuildLoanTransactions
} from "@bridgesplit/abf-react";
import { Result, LOADING_ERROR } from "@bridgesplit/utils";
import { COPY } from "app/constants";
import { BuildLoanTransactionsRequest } from "@bridgesplit/abf-sdk";
import { trackSubmitRepay } from "app/hooks";
import { TrackTransactionEvent } from "app/types";

import { allTransactionsSucceeded, useTransactionSender } from "../../transactions";
import { UnwindOption, UnwindOptionData } from "./types";

export function useCloseLoop({
    selectedQuote,
    loanExpanded,
    jitoCloseEnabled,
    selectedOption,
    loanBuilder,
    swapCollateralTx
}: {
    selectedQuote: UnwindOptionData | undefined;
    loanExpanded: AbfLoanExpanded | undefined;
    jitoCloseEnabled: boolean;
    selectedOption: UnwindOption;
    loanBuilder: ReturnType<typeof useBuildLoanTransactions>;
    swapCollateralTx: ReturnType<typeof useSwapCollateralTransaction>;
}) {
    const send = useTransactionSender();

    const closeLoop = useCallback(async () => {
        if (!selectedQuote || !selectedQuote.repayTxnParams || !loanExpanded || !selectedQuote.repayTx)
            return Result.errFromMessage(LOADING_ERROR);

        const repayTxnParams = { ...selectedQuote.repayTxnParams, simulate: false };

        if (jitoCloseEnabled) {
            const steps: BuildLoanTransactionsRequest["steps"] = [];

            if (selectedQuote.swapInstructionSteps.length > 1) {
                if (!selectedQuote.swapCollateralParams) return Result.errFromMessage(LOADING_ERROR);
                steps.push({ type: "swapCollateral", params: selectedQuote.swapCollateralParams });
            }

            steps.push({ type: "repay", params: repayTxnParams });

            const buildRequest: BuildLoanTransactionsRequest = {
                loanAddress: loanExpanded.loan.address,
                steps
            };

            const bundledUnwindTx: AbfTransactionDetails<BuildLoanTransactionsRequest> = {
                ...loanBuilder,
                description: COPY.UNWIND_TERM,
                sendOptions: {
                    ...loanBuilder.sendOptions,
                    ...selectedQuote.repayTx.sendOptions,
                    ...swapCollateralTx.sendOptions,
                    allowBatchFailure: false,
                    bundle: jitoCloseEnabled && selectedOption === "principal" ? "always" : "never",
                    refetch: () => {
                        swapCollateralTx.sendOptions.refetch?.();
                        selectedQuote.repayTx.sendOptions.refetch?.();
                    }
                }
            };

            return send(bundledUnwindTx, buildRequest, {
                description: COPY.UNWIND_TERM,
                mixpanelEvent: {
                    key: TrackTransactionEvent.SubmitRepay,
                    params: trackSubmitRepay(repayTxnParams, loanExpanded.principalMetadata)
                }
            });
        }

        if (selectedQuote.swapInstructionSteps.length > 1) {
            if (!selectedQuote.swapCollateralParams) return Result.errFromMessage(LOADING_ERROR);
            const swapRes = await send(swapCollateralTx, selectedQuote.swapCollateralParams, {
                description: "Swapping collateral",
                isMultiStep: true
            });
            if (!allTransactionsSucceeded(swapRes)) return swapRes;
        }

        return await send(selectedQuote.repayTx, repayTxnParams, {
            description: COPY.UNWIND_TERM,
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitRepay,
                params: trackSubmitRepay(selectedQuote.repayTxnParams, loanExpanded.principalMetadata)
            }
        });
    }, [
        selectedQuote,
        loanExpanded,
        jitoCloseEnabled,
        selectedOption,
        loanBuilder,
        swapCollateralTx,
        send
    ]);

    return { closeLoop };
}
